import { CheckIcon, CircleNotch, CirclePauseIcon, TrashIcon, WarningDiamondIcon } from "@xola/icons";
import { Button, Select, Table } from "@xola/ui-kit";
import React, { memo } from "react";
import {
    ACTION_LABELS,
    AdjustAction,
    AdjustmentRow,
    getMaxQty,
    InventoryStats,
    REASONS,
    RowStatus,
} from "./adjustInventoryTypes";
import { QuantitySpinner } from "@/components/Common/QuantitySpinner/QuantitySpinner";

interface AdjustmentTableRowProps {
    row: AdjustmentRow;
    preview: InventoryStats;
    stateBefore: InventoryStats;
    isInvalid: boolean;
    status: RowStatus;
    error?: string;
    onUpdate: (id: string, patch: Partial<Omit<AdjustmentRow, "id">>) => void;
    onDelete: (id: string) => void;
}
export const AdjustmentTableRow = memo(
    ({ row, preview, stateBefore, isInvalid, status, error, onUpdate, onDelete }: AdjustmentTableRowProps) => (
        <>
            <Table.Row className={isInvalid ? "!bg-danger-light" : undefined}>
                <Table.Cell className="!px-1 !py-1">
                    <Select
                        data-testid="adjustment-action-select"
                        value={row.action}
                        className="w-full truncate !pr-5 text-xs"
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                            onUpdate(row.id, { action: e.target.value as AdjustAction })
                        }
                    >
                        {(Object.keys(ACTION_LABELS) as AdjustAction[]).map((value) => (
                            <option key={value} value={value}>
                                {ACTION_LABELS[value]}
                            </option>
                        ))}
                    </Select>
                </Table.Cell>
                <Table.Cell className="!px-1 !py-1">
                    <Select
                        data-testid="adjustment-reason-select"
                        value={row.reason}
                        className="w-full truncate !pr-5 text-xs"
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                            onUpdate(row.id, { reason: e.target.value })
                        }
                    >
                        {REASONS[row.action].map((reason) => (
                            <option key={reason} value={reason}>
                                {reason}
                            </option>
                        ))}
                    </Select>
                </Table.Cell>
                <Table.Cell className="!px-1 !py-1">
                    <QuantitySpinner
                        value={row.qty}
                        min={1}
                        max={getMaxQty(row.action, stateBefore)}
                        minTooltipText="Minimum adjustment quantity is"
                        classNames={{ label: "!mx-0", button: "h-8 w-8" }}
                        onChange={(qty) => onUpdate(row.id, { qty })}
                    />
                </Table.Cell>
                <Table.Cell className="flex items-center justify-center !px-1 !py-1">
                    <span className="flex h-11 w-11 items-center justify-center rounded bg-gray-lighter px-1 font-bold text-gray-dark">
                        {preview.available}
                    </span>
                </Table.Cell>
                <Table.Cell className="!py-1 !px-2 text-center">
                    <span className="inline-block flex h-11 w-11 items-center justify-center rounded bg-gray-lighter px-1 font-bold text-gray-dark">
                        {preview.unavailable}
                    </span>
                </Table.Cell>
                <Table.Cell className="!py-1 !px-2 text-center">
                    <span className="inline-block flex h-11 w-11 items-center justify-center rounded bg-gray-lighter px-1 font-bold text-gray-dark">
                        {preview.onHand}
                    </span>
                </Table.Cell>
                <Table.Cell className="!py-1 !pl-1">
                    <Button
                        data-testid="remove-adjustment-button"
                        color="ghost"
                        size="small"
                        className="h-11 w-11 border-gray-dark text-gray-dark hover:text-red disabled:opacity-40"
                        disabled={status === "loading"}
                        onClick={() => onDelete(row.id)}
                    >
                        <TrashIcon />
                    </Button>
                </Table.Cell>
                <Table.Cell className="!py-1 text-center">
                    {status === "idle" && <CirclePauseIcon size="medium" className="text-yellow" />}
                    {status === "loading" && (
                        <CircleNotch size="medium" className="inline-block animate-spin text-gray-dark" />
                    )}
                    {status === "success" && <CheckIcon size="medium" className="inline-block text-green" />}
                    {status === "error" && <WarningDiamondIcon size="medium" className="inline-block text-red" />}
                </Table.Cell>
            </Table.Row>
            {status === "error" && error && (
                <Table.Row>
                    <Table.Cell colSpan={8} className=" !px-1 !pb-2 !pt-0">
                        <div className="rounded border border-dashed border-red px-2 py-1 text-xs text-red">
                            {error}
                        </div>
                    </Table.Cell>
                </Table.Row>
            )}
        </>
    ),
);
